import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FullScreenConfirmOverlay from './components/FullScreenConfirmOverlay';
import PageHeader from './components/PageHeader';
import Pagination from './components/Pagination';
import SortableTableHeader from './components/SortableTableHeader';
import { showToastError, showToastSuccess } from './components/ToastNotifications';
import { parseJwtPayload } from './utils/authTokens';
import { AUTH_STORAGE_KEY } from './constants';
import { getFacilities as getFacilitiesRequest } from './hooks/useFacilities';
import { getAllMarkets } from './hooks/useMarkets';
import { getOrganizationsTotals, mergeOrganization as mergeOrganizationRequest } from './hooks/useOrganizations';
import { getNextSortCriterion, type FieldSortCriterionValue } from './utils/tableSort';

interface IFacility {
    id: number | string;
    name: string;
}
interface IFilters {
    sourceSearchTerm: string;
    targetSearchTerm: string;
}
interface IMarket {
    id: number | string;
    name: string;
}
interface IOrganization {
    facilityIds: number[];
    facilityNames: string;
    id: number;
    isQuickStart: boolean;
    name: string;
    marketIds: number[];
    marketNames: string;
}
interface IPagedResult {
    data: Array<IOrganization>;
    page: number;
    pagePending: number;
    pageSize: number;
    total: number;
}

export default function OrganizationMergePage({ organizationId }: { organizationId?: number | string | null }) {
    const navigate = useNavigate();
    const token = window.localStorage.getItem(AUTH_STORAGE_KEY);
    const payload = token ? parseJwtPayload(token) : null;
    const isSuperUser = payload?.IsSuperUser === 'True' || payload?.IsSuperUser === true;

    const [facilityOptions, setFacilityOptions] = useState<IFacility[] | null>(null);
    const [filters, setFilters] = useState<IFilters>({
        sourceSearchTerm: '',
        targetSearchTerm: ''
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isMerging, setIsMerging] = useState<boolean>(false);
    const [isMergeConfirmationOpen, setIsMergeConfirmationOpen] = useState<boolean>(false);
    const [marketOptions, setMarketOptions] = useState<IMarket[] | null>(null);
    const [reloadNonce, setReloadNonce] = useState<number>(0);
    const [sourceSort, setSourceSort] = useState<FieldSortCriterionValue>({
        fieldName: 'name',
        isAscending: true,
    });
    const [sourceOrganizations, setSourceOrganizations] = useState<IPagedResult>({
        page: 0,
        pagePending: 0,
        pageSize: 12,
        total: 0,
        data: []
    });
    const [targetOrganizations, setTargetOrganizations] = useState<IPagedResult>({
        page: 0,
        pagePending: 0,
        pageSize: 12,
        total: 0,
        data: []
    });
    const [targetSort, setTargetSort] = useState<FieldSortCriterionValue>({
        fieldName: 'name',
        isAscending: true,
    });
    const [selectedSourceOrganization, setSelectedSourceOrganization] = useState<IOrganization | null>(null);
    const [selectedTargetOrganization, setSelectedTargetOrganization] = useState<IOrganization | null>(null);
    const sourceOrganizationsRequestId = useRef(0);
    const targetOrganizationsRequestId = useRef(0);

    function changeFilter(newSearchTerm, isSource) {
        if (isSource) {
            setFilters({ ...filters, sourceSearchTerm: newSearchTerm });
            setSourceOrganizations({ ...sourceOrganizations, page: 0, pagePending: 0 });
        } else {
            setFilters({ ...filters, targetSearchTerm: newSearchTerm });
            setTargetOrganizations({ ...targetOrganizations, page: 0, pagePending: 0 });
        }
    }

    function changeSort(isSource, fieldName: string) {
        if (isSource) {
            sourceOrganizationsRequestId.current += 1;
            setSourceSort((current) => getNextSortCriterion(current, fieldName));
            setSourceOrganizations((current) => ({ ...current, page: 0, pagePending: 0 }));
        } else {
            targetOrganizationsRequestId.current += 1;
            setTargetSort((current) => getNextSortCriterion(current, fieldName));
            setTargetOrganizations((current) => ({ ...current, page: 0, pagePending: 0 }));
        }
    }

    function changePage(isSource, pageOffset) {
        if (isSource) {
            setSourceOrganizations({ ...sourceOrganizations, pagePending: sourceOrganizations.page + pageOffset });
        } else {
            setTargetOrganizations({ ...targetOrganizations, pagePending: targetOrganizations.page + pageOffset })
        }
    }

    async function getOrganizations(organizations: IPagedResult, isSource: boolean) {
        if (!facilityOptions || !marketOptions) {
            return;
        }
        const requestIdRef = isSource ? sourceOrganizationsRequestId : targetOrganizationsRequestId;
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;
        const isCurrentRequest = () => requestIdRef.current === requestId;

        try {
            const { pagePending, pageSize } = organizations;
            const searchTerm = isSource ? filters.sourceSearchTerm : filters.targetSearchTerm;
            const sort = isSource ? sourceSort : targetSort;

            const payload = {
                isQuickstart: false,
                name: searchTerm?.length === 0 ? null : searchTerm,
                page: pagePending,
                pageSize,
                sortField: sort.fieldName,
                sortAscending: sort.isAscending,
            };

            setIsLoading(true);
            const response = await getOrganizationsTotals(payload);
            if (!isCurrentRequest()) return;
            setIsLoading(false);

            if (isSource) {
                setSourceOrganizations((current) => ({
                    ...current,
                    data: response.data.map(o => mapOptionsToOrganization(o as IOrganization)),
                    page: response.page,
                    pagePending: response.page,
                    pageSize: response.pageSize,
                    total: response.total
                }));
            } else {
                setTargetOrganizations((current) => ({
                    ...current,
                    data: response.data.map(o => mapOptionsToOrganization(o as IOrganization)),
                    page: response.page,
                    pagePending: response.page,
                    pageSize: response.pageSize,
                    total: response.total
                }));
            }
        } catch {
            if (!isCurrentRequest()) return;
            setIsLoading(false);
            showToastError('Could not retrieve organizations');
        }
    }

    function mapOptionsToOrganization(org: IOrganization) {
        org.marketNames = '';
        if (org.marketIds?.length) {
            for (const orgMarketId of org.marketIds) {
                for (const market of marketOptions) {
                    if (String(orgMarketId) === String(market.id)) {
                        if (org.marketNames.length > 0) {
                            org.marketNames += ', ';
                        }
                        org.marketNames += market.name;
                        break;
                    }
                }
            }
        }

        org.facilityNames = '';
        if (org.facilityIds?.length) {
            for (const orgFacilityId of org.facilityIds) {
                for (const facility of facilityOptions) {
                    if (String(orgFacilityId) === String(facility.id)) {
                        if (org.facilityNames.length > 0) {
                            org.facilityNames += ', ';
                        }
                        org.facilityNames += facility.name;
                        break;
                    }
                }
            }
        }

        return org;
    }

    async function mergeOrganization() {
        if (!isSuperUser) {
            showToastError('Only super users can merge organizations.');
            return;
        }
        if (!selectedSourceOrganization?.id) {
            showToastError('Source Organization must be selected in order to merge.');
            return;
        }
        if (!selectedTargetOrganization?.id) {
            showToastError('Target Organization must be selected in order to merge.');
            return;
        }
        if (selectedSourceOrganization.id === selectedTargetOrganization.id) {
            showToastError('Source and Target Organizations must be different in order to merge.');
            return;
        }
        try {
            setIsMerging(true);
            const response = await mergeOrganizationRequest({
                sourceOrganizationId: selectedSourceOrganization.id,
                targetOrganizationId: selectedTargetOrganization.id
            });
            setIsMerging(false);

            if (response.succeeded) {
                setSelectedSourceOrganization(null);
                setSelectedTargetOrganization(null);
                setSourceOrganizations({ ...sourceOrganizations, page: 0, pagePending: 0 });
                setTargetOrganizations({ ...targetOrganizations, page: 0, pagePending: 0 });
                setReloadNonce((value) => value + 1);
                showToastSuccess(`${selectedSourceOrganization.name} was successfully merged into ${selectedTargetOrganization.name}.`);
            } else {
                let failureReason = response.failureReason;
                if (!failureReason?.length) {
                    failureReason = response.respBody?.message?.length ? response.respBody.message : "Could not merge organization";
                }
                showToastError(failureReason);
            }
            setIsMergeConfirmationOpen(false);
        } catch {
            setIsMerging(false);
            showToastError("Could not merge organization");
        }
    }

    function promptUserToConfirmMerge(targetOrganization: IOrganization) {
        if (!isSuperUser) {
            showToastError('Only super users can merge organizations.');
            return;
        }
        setSelectedTargetOrganization(targetOrganization);
        setIsMergeConfirmationOpen(true);
    }

    function selectSourceOrganization(organization: IOrganization) {
        if (!isSuperUser) {
            showToastError('Only super users can merge organizations.');
            return;
        }
        setSelectedSourceOrganization(organization);
        setFilters({ ...filters, targetSearchTerm: '' });
        setTargetOrganizations({ ...targetOrganizations, page: 0, pagePending: 0 });
    }

    useEffect(() => {
        async function getResults() {
            try {
                if (!organizationId) return;
                const response = await getAllMarkets(organizationId);
                setMarketOptions(response.sort((a, b) => a.name.localeCompare(b.name)));
            } catch {
                showToastError('Error trying to obtain markets');
            }
        }
        if (organizationId)
            getResults();
    }, [organizationId])

    useEffect(() => {
        async function loadFacilities() {
            try {
                if (!organizationId) return;
                const response = await getFacilitiesRequest(organizationId);
                setFacilityOptions(
                    response.map((facility) => ({
                        id: facility?.id ?? '',
                        name: facility?.name || '',
                    }))
                );
            } catch {
                setFacilityOptions([]);
                showToastError('Error trying to obtain facilities');
            }
        }
        if (organizationId)
            loadFacilities();
    }, [organizationId])

    useEffect(() => {
        getOrganizations(sourceOrganizations, true);
    }, [facilityOptions, marketOptions, sourceOrganizations.pagePending, filters.sourceSearchTerm, sourceSort.fieldName, sourceSort.isAscending, reloadNonce]);

    useEffect(() => {
        getOrganizations(targetOrganizations, false);
    }, [facilityOptions, marketOptions, targetOrganizations.pagePending, filters.targetSearchTerm, targetSort.fieldName, targetSort.isAscending, reloadNonce]);

    function renderOrganizationsSearch(isSource) {
        const searchTerm = isSource ? filters.sourceSearchTerm : filters.targetSearchTerm;
        return (
            <div className="card filter-card">
                <div className="filter-bar">
                    <div className="filter-left">
                        <input
                            className="input filter-search"
                            placeholder={`Search ${isSource ? 'Source' : 'Target'} organizations`}
                            value={searchTerm}
                            onChange={(e) => changeFilter(e.target.value, isSource)}
                            data-testid={`organization-search-${isSource ? 'source' : 'target'}`}
                        />
                    </div>
                </div>
            </div>
        );
    }

    function renderOrganizationsTable(isSource) {
        const organizations = isSource ? sourceOrganizations : targetOrganizations;
        const sort = isSource ? sourceSort : targetSort;
        return (
            <div>
                <table className="table">
                    <thead>
                        <tr>
                            <SortableTableHeader label={`${isSource ? 'Source' : 'Target'} Organization`} fieldName="name" sort={sort} onSort={(fieldName) => changeSort(isSource, fieldName)} />
                            <SortableTableHeader label="Market" fieldName="marketNames" sort={sort} onSort={(fieldName) => changeSort(isSource, fieldName)} />
                            <SortableTableHeader label="Facility" fieldName="facilityNames" sort={sort} onSort={(fieldName) => changeSort(isSource, fieldName)} />
                            <th aria-label="actions" />
                        </tr>
                    </thead>
                    <tbody>
                        {organizations.data.map((org) => (
                            <tr key={org.id}>
                                <td>{org.name}</td>
                                <td>{org.marketNames}</td>
                                <td>{org.facilityNames}</td>
                                <td style={{ textAlign: 'right' }}>
                                    {isSuperUser && (isSource ?
                                        (<button className="button" disabled={isLoading} onClick={() => selectSourceOrganization(org)}>
                                            Select
                                        </button>) : (
                                        <button className="button" disabled={isLoading || selectedSourceOrganization?.id === org.id} onClick={() => promptUserToConfirmMerge(org)}>
                                            Merge
                                        </button>
                                    ))}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <Pagination
                    page={organizations.page}
                    totalPages={Math.max(1, Math.ceil(organizations.total / organizations.pageSize))}
                    onPrev={() => { changePage(isSource, -1) }}
                    onNext={() => { changePage(isSource, 1) }}
                    format="of"
                    prevDisabled={isLoading || isMerging}
                    nextDisabled={isLoading || isMerging}
                    hideWhenSingle={false}
                    prevLabel="◀"
                    nextLabel="▶"
                />
            </div>
        )
    }

    return (
        <div className="stack" data-testid="page-organizations-merge">
            <FullScreenConfirmOverlay
                show={isMergeConfirmationOpen}
                title="Merge Organization"
                message={isMerging ?
                    `Merging ${selectedSourceOrganization?.name} into ${selectedTargetOrganization?.name}. This may take a few moments.` :
                    `Are you sure you want to merge ${selectedSourceOrganization?.name} into ${selectedTargetOrganization?.name}? This action cannot be undone.`}
                cancelLabel="Cancel"
                confirmLabel={isMerging ? 'Merging…' : 'Yes, merge permanently'}
                busy={isMerging}
                cancelHidden={isMerging}
                onCancel={() => {
                    if (isMerging) return;
                    setIsMergeConfirmationOpen(false);
                }}
                onConfirm={async () => {
                    if (isMerging) return;
                    if (!isSuperUser) return;
                    mergeOrganization();
                }}
                testId="organizations-merge-overlay"
            />

            <PageHeader
                title="Merge Organizations"
                actions={
                    <div>
                        {selectedSourceOrganization ? (
                            <div>
                                <span>Source Organization Selected: {selectedSourceOrganization.name}</span>
                                &nbsp;
                                {isMerging || !isSuperUser ? '' : (
                                    <button className="button" onClick={() => setSelectedSourceOrganization(null)}>Cancel Merge</button>
                                )}
                            </div>
                        ) : ''}
                        <button type="button" className="button" onClick={() => navigate('/internal')}>
                            Back
                        </button>
                    </div>
                }
            />

            {selectedSourceOrganization ? renderOrganizationsSearch(false) : renderOrganizationsSearch(true)}

            <div className="card">
                {selectedSourceOrganization ? renderOrganizationsTable(false) : renderOrganizationsTable(true)}
            </div>
        </div>
    );
}
